import { QueryCache, QueryClient } from "@tanstack/react-query"
import {
  isExpectedApiError,
  isForbidden,
  isUnauthorized,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"

// A kiosk tablet can sit untouched for hours; the OS commonly suspends its Wi-Fi
// radio during that time, so the first request after someone walks up often hits
// a connection that hasn't been re-established yet. Retrying a few times absorbs
// that wake-up blip without waiting so long that a genuinely down server looks
// stuck for minutes (#327).
//
// Six attempts over React Query's default delays — 1s, 2s, 4s, 8s, 16s, 30s,
// about a minute in total. That schedule is not a coincidence: this app had
// already arrived at exactly those numbers by hand, and the library computes
// them from the same formula.
const MAX_NETWORK_RETRIES = 6

/**
 * This app's query client.
 *
 * Its defaults are the opposite of the practitioner portal's on purpose. That
 * app polls, so the poll is its retry and retrying inside a tick only muddies
 * the timing. Here nothing polls and a single request decides whether a tablet
 * can be used at all, so retrying is the whole point.
 *
 * An answer from last time is not one to act on: these queries decide whether a
 * screen may be seen at all. That is enforced in the guards, which render
 * nothing while a check is in flight, rather than by keeping the cache empty —
 * `gcTime: 0` reads like it would do the job and does not, because collection is
 * scheduled rather than immediate.
 *
 * Built per call rather than shared, so a test gets a cache nobody else has
 * written to.
 */
export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // A 401 is an answer, not a fault — the caller redirects on it.
        if (isUnauthorized(error)) return
        // A 403 is an answer too on a screen that asks whether a role may see
        // it, and those screens say so. It stays a fault everywhere else: on
        // the kiosk a 403 is the WAF, where this log is the only breadcrumb.
        if (isForbidden(error) && query.meta?.forbiddenIsExpected) return
        // A screen whose every 4xx is an answer — a kiosk session that has
        // closed or been fully answered — says so, and only its faults are
        // reported.
        if (isExpectedApiError(error) && query.meta?.clientErrorsAreExpected)
          return
        trackError(error, {
          source: String(query.meta?.source ?? query.queryKey[0]),
        })
      },
    }),
    defaultOptions: {
      queries: {
        // Only a failure worth asking again: a dropped connection, a 5xx, a
        // reply that would not parse. A 4xx is the server's answer — a refused
        // credential, an expired link, a rate limit — and retrying it only
        // delays saying so, by about a minute, or for a 429 makes it worse.
        retry: (failureCount, error) =>
          failureCount < MAX_NETWORK_RETRIES && !isExpectedApiError(error),

        // React Query's own defaults, restated rather than inherited (ADR-031),
        // because they are behaviour every screen here gets: an answer is
        // stale at once, and is fetched again when the screen becomes visible
        // (a kiosk tablet waking counts) and when the connection comes back (its
        // Wi-Fi rejoining after sleep). A screen whose answer must not change
        // under the person using it — a form being filled in — turns these off
        // for its own query rather than here.
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
    },
  })
